
import React, { useState, useEffect, useRef } from 'react';
import { MoreHorizontal, Edit2, Trash2, Loader2, Archive } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { RenameAnalysisModal } from '@/components/RenameAnalysisModal';
import { DeleteAnalysisModal } from '@/components/DeleteAnalysisModal';
import { AnalysisListSkeleton } from '@/components/AnalysisListSkeleton';
import { useTranslation } from 'react-i18next';

interface Analysis {
  id: number;
  attributes: {
    patient_name: string;
    subject?: string;
    createdAt: string;
    template: {
      data: {
        attributes: {
          specialty: string;
        };
      };
    };
  };
}

interface AnalysisListProps {
  analyses: Analysis[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onNewAnalysis: () => void;
  onRename?: (id: number) => void;
  onDelete?: (id: number) => void;
  onArchive?: (id: number) => void;
  onRefresh?: () => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onCloseSidebar?: () => void;
  isLoading?: boolean;
}

export const AnalysisList = ({ 
  analyses, 
  selectedId, 
  onSelect, 
  onNewAnalysis,
  onRename,
  onDelete,
  onArchive,
  onRefresh,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  onCloseSidebar,
  isLoading = false
}: AnalysisListProps) => {
  const { t } = useTranslation();
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analysis | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);

  const handleMenuAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  const handleRename = (analysis: Analysis) => {
    setSelectedAnalysis(analysis);
    setRenameModalOpen(true);
    setOpenDropdownId(null); // Close the dropdown
    // Close sidebar on mobile when modal opens
    onCloseSidebar?.();
  };

  const handleDelete = (analysis: Analysis) => {
    setSelectedAnalysis(analysis);
    setDeleteModalOpen(true);
    setOpenDropdownId(null); // Close the dropdown
    // Close sidebar on mobile when modal opens
    onCloseSidebar?.();
  };

  const handleArchive = (analysis: Analysis) => {
    if (onArchive) {
      onArchive(analysis.id);
    }
    setOpenDropdownId(null); // Close the dropdown
    // Close sidebar on mobile when action is performed
    onCloseSidebar?.();
  };

  const handleRenameSuccess = () => {
    onRefresh?.();
  };

  const handleDeleteSuccess = () => {
    onRefresh?.();
  };

  // Handle scroll events for infinite loading
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    if (isNearBottom && hasNextPage && !isFetchingNextPage && onLoadMore) {
      console.log('Loading more analyses...');
      onLoadMore();
    }
  };

  // Auto-load more when component mounts if there are very few items
  useEffect(() => {
    if (analyses.length < 10 && hasNextPage && !isFetchingNextPage && onLoadMore) {
      console.log('Auto-loading more analyses due to low count...');
      onLoadMore();
    }
  }, [analyses.length, hasNextPage, isFetchingNextPage, onLoadMore]);

  return (
    <div className="flex flex-col h-full">
      {/* Analyses List - ScrollArea with proper spacing */}
      <div className="flex-1 min-h-0 px-2">
        <ScrollArea 
          className="h-full"
          onScrollCapture={handleScroll}
        >
          {isLoading ? (
            <AnalysisListSkeleton />
          ) : (
            <div className="space-y-3 py-6">
              {analyses.map((analysis) => (
                <div
                  key={analysis.id}
                  onClick={() => onSelect(analysis.id)}
                  className={`
                    p-4 rounded-lg cursor-pointer transition-colors border
                    ${selectedId === analysis.id 
                      ? 'bg-primary/10 border-primary/20' 
                      : 'bg-card hover:bg-muted/50 border-border'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">
                        {analysis.attributes.subject || analysis.attributes.patient_name || t('no_subject')}
                      </p>
                    </div>
                    <DropdownMenu 
                      open={openDropdownId === analysis.id} 
                      onOpenChange={(open) => setOpenDropdownId(open ? analysis.id : null)}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 opacity-60 hover:opacity-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent 
                        align="start" 
                        side="bottom"
                        className="w-32 z-[99999] bg-white border border-gray-200 shadow-lg rounded-md"
                        sideOffset={2}
                        alignOffset={-20}
                      >
                        <DropdownMenuItem
                          onClick={(e) => handleMenuAction(e, () => handleRename(analysis))}
                          className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          <Edit2 className="mr-2 h-4 w-4" />
                          {t('rename')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => handleMenuAction(e, () => handleArchive(analysis))}
                          className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          <Archive className="mr-2 h-4 w-4" />
                          {t('archive')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => handleMenuAction(e, () => handleDelete(analysis))}
                          className="cursor-pointer px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}

              {/* Loading indicator for infinite scroll */}
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">{t('loading_more_analyses')}</span>
                </div>
              )}

              {/* End of list indicator - only show if 10 or more analyses */}
              {!hasNextPage && analyses.length >= 10 && !isLoading && (
                <div className="flex items-center justify-center py-6">
                  <span className="text-xs text-muted-foreground">
                    {analyses.length === 1 ? t('one_analysis_loaded') : t('analyses_loaded', { count: analyses.length })}
                  </span>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Rename Modal */}
      <RenameAnalysisModal
        isOpen={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        onSuccess={handleRenameSuccess}
        selectedAnalysis={selectedAnalysis}
      />

      {/* Delete Modal */}
      <DeleteAnalysisModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
        analysis={selectedAnalysis}
      />
    </div>
  );
};
